import { useSnackbar } from 'notistack'
import * as React from 'react'
import { EQUIPMENT_DEFAULT_WHERE_ARGS } from '../../components/GravityForms/Fields/searchers/EquipmentSearchField'
import { OFFLINE_DATA_BATCH_SIZE } from '../../constants'
import {
  RootQueryToEquipmentConnectionEdge,
  useAllEquipmentLazyQuery,
} from '../../types/generated'
import { UseOfflineDataReturnValue } from '../../types/offlineData'
import usePrevious from '../usePrevious'

const useOfflineEquipment = (
  setOfflineData: React.AsyncDispatch<
    React.SetStateAction<RootQueryToEquipmentConnectionEdge[]>
  >
): UseOfflineDataReturnValue => {
  const { enqueueSnackbar } = useSnackbar()
  const [fetching, setFetching] = React.useState(false)
  const batchSize = OFFLINE_DATA_BATCH_SIZE
  const [isLoading, setIsLoading] = React.useState(false)
  const startLoading = () => setIsLoading(true)
  const stopLoading = () => setIsLoading(false)

  const [getEquipment, { refetch, called, fetchMore, loading, data }] =
    useAllEquipmentLazyQuery({
      variables: {
        first: batchSize,
        after: null,
        where: EQUIPMENT_DEFAULT_WHERE_ARGS,
      },
      notifyOnNetworkStatusChange: true,
    })

  const shouldFetchMore =
    (called && !loading && data?.allEquipment?.pageInfo?.hasNextPage) ?? false

  if (shouldFetchMore && fetchMore) {
    fetchMore({
      variables: {
        first: batchSize,
        after: data?.allEquipment?.pageInfo?.endCursor,
      },
    })
  }

  const doneFetching =
    ((!loading &&
      called &&
      data &&
      !data?.allEquipment?.pageInfo?.hasNextPage) ||
      data?.allEquipment?.edges?.length === 0) ??
    false
  const prevDoneFetching = usePrevious(
    doneFetching && data?.allEquipment?.pageInfo?.hasPreviousPage
  )

  React.useEffect(() => {
    if (doneFetching && !prevDoneFetching && fetching) {
      const edges = data?.allEquipment?.edges ?? []
      enqueueSnackbar(`${edges?.length} Equipment downloaded`)
      setTimeout(() => {
        // @ts-expect-error this is fine
        setOfflineData(edges).then(stopLoading).catch(stopLoading)
        setFetching(false)
      }, 300)
    }
    // eslint-disable-next-line
  }, [prevDoneFetching, doneFetching, enqueueSnackbar, fetching])

  return {
    getOfflineData: () => {
      setFetching(true)
      startLoading()
      !called ? getEquipment() : refetch?.()
    },
    loading: isLoading,
    done: doneFetching,
    called: called,
  }
}

export default useOfflineEquipment
